import type { FlightState } from '../types';
import { addVec3, scaleVec3, subVec3, normalizeVec3 } from './vec3Math';

/** First descending crossing of surface height. Works for chips shorter than
 * 0.1s too; never extrapolates from an already-below-ground state. */
export function interpolateGroundContact(previous: FlightState, next: FlightState): FlightState | null {
  if (previous.position.y < 0 || next.position.y > 0 || next.velocity.y >= 0) return null;
  const span = previous.position.y - next.position.y;
  let t = span > 0 ? previous.position.y / span : 0;
  const dt = next.time - previous.time;
  // A tiny launch may rise and land within one step. Linear interpolation
  // would incorrectly select the launch root (t=0). Fit a quadratic through
  // initial height/vertical velocity and final height, then take its second root.
  const substepArc = previous.position.y === 0 && previous.velocity.y > 0 && dt > 0;
  if (substepArc) t = previous.velocity.y * dt / (previous.velocity.y * dt - next.position.y);
  const position = substepArc
    ? addVec3(previous.position, addVec3(scaleVec3(previous.velocity, dt * t),
        scaleVec3(subVec3(subVec3(next.position, previous.position), scaleVec3(previous.velocity, dt)), t * t)))
    : addVec3(previous.position, scaleVec3(subVec3(next.position, previous.position), t));
  position.y = 0;
  return {
    position,
    velocity: addVec3(previous.velocity, scaleVec3(subVec3(next.velocity, previous.velocity), t)),
    spinRate: previous.spinRate + (next.spinRate - previous.spinRate) * t,
    spinAxis: normalizeVec3(addVec3(previous.spinAxis, scaleVec3(subVec3(next.spinAxis, previous.spinAxis), t))),
    time: previous.time + (next.time - previous.time) * t,
  };
}
